export interface MediaSessionHandlers {
  onPlay: () => void
  onPause: () => void
  onPrevious: () => void
  onNext: () => void
}

const ACTIONS = ['play', 'pause', 'previoustrack', 'nexttrack'] as const

function getMediaSession(): MediaSession | null {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator
    ? navigator.mediaSession
    : null
}

/** Registers the article on the OS media controls (lock screen, headset
 * buttons, notification shade). Returns a function that removes everything.
 * A no-op where the Media Session API is missing. */
export function bindMediaSession(
  info: { title: string; artist: string },
  handlers: MediaSessionHandlers,
): () => void {
  const session = getMediaSession()
  if (!session) return () => {}

  if (typeof MediaMetadata === 'function') {
    session.metadata = new MediaMetadata({ title: info.title, artist: info.artist })
  }
  const bindings: Record<(typeof ACTIONS)[number], () => void> = {
    play: handlers.onPlay,
    pause: handlers.onPause,
    previoustrack: handlers.onPrevious,
    nexttrack: handlers.onNext,
  }
  for (const action of ACTIONS) {
    try {
      session.setActionHandler(action, bindings[action])
    } catch {
      // Action not supported by this browser.
    }
  }

  return () => {
    for (const action of ACTIONS) {
      try {
        session.setActionHandler(action, null)
      } catch {
        // Action not supported by this browser.
      }
    }
    session.metadata = null
    session.playbackState = 'none'
  }
}

export function setMediaSessionPlaybackState(state: 'playing' | 'paused'): void {
  const session = getMediaSession()
  if (session) session.playbackState = state
}
